import { useTradesHeader } from "features/trades/common/TradesHeader";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTradesHeaderHeight } from "features/trades/common/commonHooks";
import { useRouteMatch } from "react-router-dom";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { CLOSED_TRADES_PATH, OPEN_TRADES_PATH } from "common/constants";
import TradesTableContent from "features/trades/TradesTableContent";
import TwoChartsTableLayout, {
  MOBILE_LAYOUT_ONE_COLUMN,
} from "common/Layouts/TwoChartsTableLayout";
import useMedia from "utils/hooks/useMedia";
import { getTradesFilterTypeToTitle } from "@shared/features/trades/common/utils";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import styled, { useTheme } from "styled-components";
import TradePageProvider from "@shared/features/trades/common/TradesPageProvider";
import {
  useBottomNavigator,
  useDisablePageScroll,
  useOnReconnect,
} from "utils/hooks/misc";
import TradeActionsProvider from "features/trades/OpenTrades/TradeActionsProvider";
import { TradeActionsContext } from "@shared/features/trades/common/contexts";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import PageTabs, { TabItem } from "common/Tabs/PageTabs";
import { TradesTab } from "@shared/features/trades/common/types";
import {
  closedTradesFilters,
  openTradesFilters,
} from "@shared/slices/filters.slice";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips";
import MobileTradesCards from "features/trades/TradesCards/MobileTradesCards";
import BuyButton from "common/Buttons/BuyButton";
import Flex from "common/Boxes/Flex";
import TradesCharts from "features/trades/TradesCharts/TradesCharts.tsx";
import TradesPieChart from "features/trades/TradesCharts/TradesPieChart.tsx";
import ClosedTradesStatistics from "features/trades/ClosedTrades/ClosedTradesStatistics.tsx";
import { PageTabsContext } from "common/Tabs/PageTabs.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  useDisplayTradeDetails,
  useIsSubUser,
  useSelectUser,
} from "@shared/utils/hooks/misc.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import NewLookTradesDesktop from "features/trades/NewLook/NewLookTradesDesktop.tsx";
import SocketPnlProvider from "@shared/features/dashboard/SocketPnlProvider";
import { skipToken } from "@reduxjs/toolkit/query";
import { PhoneChipsFade } from "features/dashboard/PhoneDashboard/PhoneSummary";
import {
  PhoneClosedTradesHero,
  PhoneOpenTradesHero,
} from "features/trades/NewLook/PhoneTradesHero.tsx";

const TradesPage = () => {
  const urlIsOpen = !!useRouteMatch(OPEN_TRADES_PATH);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  useDisablePageScroll(isBottomNavigator);
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const [tab, setTab] = useState(TradesTab.OPEN);

  const getContent = useCallback(
    (isOpen: boolean) => {
      return (
        <TradePageProvider
          isOpenTrades={isOpen}
          useOnReconnect={useOnReconnect}
        >
          <TradeActionsProvider>
            <InnerTradesPage tab={tab} />
          </TradeActionsProvider>
        </TradePageProvider>
      );
    },
    [tab]
  );

  const mobileTabItems = useMemo(() => {
    if (!isBottomNavigator) return [];
    return [
      {
        key: TradesTab.OPEN,
        label: t("tabs.open"),
        children: getContent(true),
        link: OPEN_TRADES_PATH,
      },
      {
        key: TradesTab.CLOSED,
        label: t("tabs.closed"),
        children: getContent(false),
        link: CLOSED_TRADES_PATH,
      },
    ];
  }, [isBottomNavigator, getContent, i18n.language]);

  const onTabChange = useCallback(
    (tabItem: TabItem) => setTab(tabItem.key as TradesTab),
    []
  );

  if (isBottomNavigator) {
    return <PageTabs items={mobileTabItems} onTabChange={onTabChange} />;
  }

  return getContent(urlIsOpen);
};

export default React.memo(TradesPage);

interface InnerTradesPageProps {
  tab: TradesTab;
}

const InnerTradesPage = ({ tab }: InnerTradesPageProps) => {
  const theme = useTheme();
  const { setTabCount } = useContext(PageTabsContext) || {};
  const {
    isOpenTrades,
    activeTrades,
    waitingCancellationTrades,
    isLoading,
    filtersSlice,
    filteredOpenTradesData,
    getClosedTradesQuery,
    getOpenTradesQuery,
  } = useContext(TradesPageContext)!;
  const { isBulkMode } = useContext(TradeActionsContext)!;
  const { analytics } = useContext(AnalyticsContext);
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_LAYOUT_ONE_COLUMN})`],
    [true],
    false
  );
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const user = useSelectUser();
  const displayTradeDetails = useDisplayTradeDetails();

  const tradesHeaderData = useTradesHeader(isOpenTrades);
  const {
    tradesHeader,
    filtersExpanded,
    setFiltersExpanded,
    exportButton,
    onExportClick,
    isExporting,
    noExchangeConnectedModal,
    onCreateClick,
  } = tradesHeaderData;
  const tradesHeaderHeight = useTradesHeaderHeight();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const [chartFetching, setChartFetching] = useState(false);
  // New look (desktop): filters open in a side sheet instead of a column.
  const [newLookFiltersOpen, setNewLookFiltersOpen] = useState(false);
  const isSubUser = useIsSubUser();

  const { data: dataOpen } = getOpenTradesQuery || {};
  const { data: dataClosed } = getClosedTradesQuery || {};
  const numOpenTrades = filteredOpenTradesData?.length || 0;
  const numClosedTrades =
    getClosedTradesQuery?.data?.aggregated_data?.number_of_closed_trades || 0;
  const numTrades = isOpenTrades ? numOpenTrades : numClosedTrades;
  const hideCount =
    (isOpenTrades && !numOpenTrades && !dataOpen) ||
    (!isOpenTrades && !numClosedTrades && !dataClosed);

  useEffect(() => {
    setTabCount?.(
      isOpenTrades ? TradesTab.OPEN : TradesTab.CLOSED,
      hideCount ? undefined : numTrades
    );
  }, [isOpenTrades, numTrades, hideCount]);

  useEffect(() => {
    if (isOpenTrades) {
      analytics?.openTradesView({} as any);
    } else {
      analytics?.closedTradesView({} as any);
    }
  }, [analytics, isOpenTrades]);

  usePageWrapper({
    filters: {
      loading: isLoading,
      selectors: filtersSlice?.selectors,
      actions: filtersSlice?.actions,
      filterTypeToTitle: getTradesFilterTypeToTitle(isOpenTrades),
    },
    showSearch: true,
    hideMobileFilters: true,
    dependencies: [isOpenTrades, isLoading, tab],
    skip:
      isBottomNavigator &&
      ((isOpenTrades && tab !== TradesTab.OPEN) ||
        (!isOpenTrades && tab === TradesTab.OPEN)),
  });

  const getMainContent = () => {
    const mainContent = [
      <TradesTableContent
        key="activeTrades"
        data={activeTrades}
        isOpenTrades={isOpenTrades}
        searchPlaceholder={tCommon("labels.search_placeholder")}
        titleOrNode={t("table.title", {
          status: t(isOpenTrades ? "tabs.open" : "tabs.closed"),
        })}
        allowBulkActions={isOpenTrades}
        openFilters={() =>
          theme.isNewLook && !isBottomNavigator
            ? setNewLookFiltersOpen(true)
            : setFiltersExpanded(true)
        }
        allowTradeClick={displayTradeDetails}
      />,
    ];
    if (waitingCancellationTrades?.length) {
      mainContent.splice(
        0,
        0,
        <TradesTableContent
          key="waitingCancellationTrades"
          data={waitingCancellationTrades}
          isOpenTrades={isOpenTrades}
          searchPlaceholder={tCommon("labels.search_placeholder")}
          titleOrNode={t("table.pending_closed_title")}
          allowBulkActions={false}
          hideActions={true}
          allowTradeClick={displayTradeDetails}
          helpTooltip={
            <HelperTooltip
              analyticsFieldName={t("table.pending_closed_title")}
              analyticsPageName={AnalyticsPageName.TRADES}
              description={t("table.pending_closed_tooltip")}
              iconStyle={{ color: theme.colors.secondaryGrey }}
            />
          }
        />
      );
    }
    return mainContent;
  };

  let topRowHeight: number | string | undefined;
  if (isOpenTrades) {
    topRowHeight = isMobile ? "minmax(max-content, 300px)" : undefined;
  } else {
    topRowHeight = isMobile ? "max-content" : 350;
  }

  const filtersChips = useMemo(() => {
    if (!isBottomNavigator) return;
    const filters = isOpenTrades ? openTradesFilters : closedTradesFilters;
    return (
      <FiltersChips
        filtersActions={filters.actions}
        filtersSelectors={filters.selectors}
        filterTypeToTitle={getTradesFilterTypeToTitle(isOpenTrades)}
      />
    );
  }, [isBottomNavigator, isOpenTrades]);

  const renderTopContainer = useCallback(() => {
    if (theme.isNewLook && isBottomNavigator)
      return isOpenTrades ? <PhoneOpenTradesHero /> : <PhoneClosedTradesHero />;
    const showExport = !IS_TELEGRAM_WEB_APP && !isSubUser;
    if (theme.isNewLook && isOpenTrades) {
      return (
        <TradesCharts
          isOpenTrades={isOpenTrades}
          setChartFetching={setChartFetching}
          headerAction={
            showExport && (
              <CompactButtonWrapper>{exportButton}</CompactButtonWrapper>
            )
          }
        />
      );
    }
    return (
      <>
        {showExport && (
          <ExportButtonWrapper $isNewLook={theme.isNewLook}>
            {exportButton}
          </ExportButtonWrapper>
        )}
        <TradesCharts
          isOpenTrades={isOpenTrades}
          setChartFetching={setChartFetching}
        />
      </>
    );
  }, [exportButton, isOpenTrades, theme.isNewLook, isBottomNavigator]);

  if (isBottomNavigator) {
    const mobileContent = (
      <MobileWrapper>
        {theme.isNewLook ? (
          <PhoneChipsFade>{filtersChips}</PhoneChipsFade>
        ) : (
          filtersChips
        )}
        <MobileTradesCards
          chartFetching={chartFetching}
          renderTopContainer={renderTopContainer}
          phone={theme.isNewLook}
          onExport={
            !IS_TELEGRAM_WEB_APP && !isSubUser ? onExportClick : undefined
          }
          isExporting={isExporting}
        />
        {!isBulkMode && !isSubUser && !user?.isWhopExperience() && (
          <BuyButton onClick={onCreateClick} />
        )}
        {noExchangeConnectedModal}
      </MobileWrapper>
    );
    // The hero's unrealized PnL follows the live feed the rows use.
    return theme.isNewLook && isOpenTrades ? (
      <SocketPnlProvider
        getTradesArgs={skipToken}
        openTradesData={activeTrades}
      >
        {mobileContent}
      </SocketPnlProvider>
    ) : (
      mobileContent
    );
  }

  if (theme.isNewLook) {
    return (
      <NewLookTradesDesktop
        isOpenTrades={isOpenTrades}
        mainContent={getMainContent()}
        header={tradesHeaderData}
        filtersOpen={newLookFiltersOpen}
        setFiltersOpen={setNewLookFiltersOpen}
      />
    );
  }

  const smallChartUpperLeft = isOpenTrades ? (
    <TradesPieChart />
  ) : (
    <ClosedTradesStatistics />
  );

  const largeChartUpperMiddle = <TradesCharts isOpenTrades={isOpenTrades} />;

  return (
    <>
      {tradesHeader}
      <TwoChartsTableLayout
        smallChartUpperLeft={smallChartUpperLeft}
        largeChartUpperMiddle={largeChartUpperMiddle}
        mobileCharts={[largeChartUpperMiddle, ...getMainContent()]}
        mainContent={getMainContent()}
        useMobileCards={false}
        filtersExpanded={filtersExpanded}
        distanceFromHeader={tradesHeaderHeight}
        topRowHeight={topRowHeight}
        hideCharts={isBulkMode}
        hideLargeChartCard={true}
      />
    </>
  );
};

const MobileWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const ExportButtonWrapper = styled(Flex)<{ $isNewLook?: boolean }>`
  justify-content: flex-end;
  margin-bottom: 12px;
  margin-top: -4px;
  ${(p) =>
    p.$isNewLook &&
    `
    margin: 0 0 12px;
    .ant-btn {
      height: 30px;
      padding: 0 12px;
      font-size: 13px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
    }
  `}
`;
const CompactButtonWrapper = styled.div`
  flex: none;
  .ant-btn {
    height: 28px;
    padding: 0 10px;
    font-size: 12.5px;
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
    gap: 6px;
  }
  .ant-btn svg {
    width: 14px;
    height: 14px;
  }
`;
